import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TicketPercent, PlusCircle, Users, ShoppingBag, Power, Download } from "lucide-react";
import { api, errMsg, formatR } from "@/lib/api";

const EMPTY_CODE = { code: "", description: "", discount_type: "percent", discount_value: "", min_subtotal: "", expires_at: "" };

function PromoGroup({ title, products, tone, onEnd }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid={`promo-group-${tone}`}>
      <h3 className="font-display font-bold text-navy mb-4">
        {title} <span className="text-xs font-mono text-slate-400 ml-1">{products.length}</span>
      </h3>
      {products.length === 0 ? (
        <p className="text-sm text-slate-400">None.</p>
      ) : (
        <div className="space-y-2.5">
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 text-sm border border-slate-100 rounded-xl p-3" data-testid={`promo-${p.sku || p.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate">{p.name}</p>
                <p className="text-xs text-slate-400 font-mono">
                  <span className="line-through">{formatR(p.price)}</span> → <span className="text-brand font-bold">{formatR(p.promo_price)}</span>
                  {p.promo_end && <span className="ml-2">ends {new Date(p.promo_end).toLocaleDateString("en-ZA")}</span>}
                </p>
              </div>
              <button onClick={() => onEnd(p)} className="text-xs font-bold text-red-500 hover:underline shrink-0" data-testid={`end-promo-${p.sku || p.id}`}>
                End promo
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPromotions() {
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState(null);
  const [form, setForm] = useState(EMPTY_CODE);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/promotions").then((r) => setData(r.data)).catch(() => {});
  const loadCodes = () => api.get("/admin/promo-codes").then((r) => setCodes(r.data.codes)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    loadCodes();
  }, []);

  const endPromo = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, {
        name: p.name, sku: p.sku || "", barcode: p.barcode || "", isbn: p.isbn || "",
        category: p.category, subcategory: p.subcategory || "", description: p.description || "",
        image: p.image || "", price: p.price, cost_price: p.cost_price ?? null,
        promo_price: null, promo_start: null, promo_end: null,
        stock: p.stock, supplier: p.supplier || "", featured: !!p.featured, status: p.status,
      });
      toast.success(`Promotion ended on "${p.name}"`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createCode = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code.trim(), description: form.description.trim(),
        discount_type: form.discount_type, discount_value: parseFloat(form.discount_value),
        min_subtotal: form.min_subtotal === "" ? 0 : parseFloat(form.min_subtotal),
        expires_at: form.expires_at || null,
      });
      toast.success(`Code ${form.code.trim().toUpperCase()} created — ready to share`);
      setForm(EMPTY_CODE);
      loadCodes();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCreating(false);
    }
  };

  const toggleCode = async (c) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, {
        code: c.code, description: c.description || "", discount_type: c.discount_type,
        discount_value: c.discount_value, min_subtotal: c.min_subtotal || 0,
        expires_at: c.expires_at ? c.expires_at.slice(0, 10) : null, active: !c.active,
      });
      toast.success(`${c.code} ${c.active ? "deactivated" : "activated"}`);
      loadCodes();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const downloadCard = (c) => {
    api.get(`/admin/promo-codes/${c.id}/card.pdf`, { responseType: "blob" })
      .then((r) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(r.data);
        a.download = `promo-card-${c.code}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`Printable card for ${c.code} downloaded`);
      })
      .catch((e) => toast.error(errMsg(e)));
  };

  if (!data) return <div className="h-40 bg-white rounded-2xl border border-slate-100 animate-pulse" />;

  return (
    <div data-testid="admin-promotions" className="space-y-8">
      <section data-testid="promo-codes-section">
        <h2 className="font-display text-xl font-bold text-navy">Promo codes</h2>
        <p className="text-sm text-slate-500 mt-1 mb-4">
          Customers enter a code at checkout for a discount — and each code doubles as a campaign tracker, so you can see exactly how many customers and how much revenue each activation brought in (e.g. <span className="font-mono font-bold text-navy">WOND1260</span> handed out at Wonderpark Mall vs <span className="font-mono font-bold text-navy">WOND3288</span> at another location).
        </p>

        <form onSubmit={createCode} className="bg-white rounded-3xl border border-slate-200/80 p-5 mb-5" data-testid="promo-code-form">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Issue a new code</p>
          <div className="flex gap-2 flex-wrap items-center">
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="CODE e.g. WOND1260" className="w-40 h-10 rounded-xl border border-slate-200 px-3 text-sm font-mono font-bold uppercase" data-testid="promo-code-input" />
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Campaign e.g. Wonderpark Mall activation" className="flex-1 min-w-[200px] h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="promo-code-desc" />
            <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="h-10 rounded-xl border border-slate-200 px-2.5 text-sm bg-white" data-testid="promo-code-type">
              <option value="percent">% off</option>
              <option value="fixed">R off</option>
            </select>
            <input required type="number" min="1" step="0.01" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              placeholder={form.discount_type === "percent" ? "e.g. 10" : "e.g. 50"}
              className="w-24 h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="promo-code-value" />
            <input type="number" min="0" step="0.01" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })}
              placeholder="Min order R (optional)" className="w-40 h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="promo-code-min" />
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-500" title="Expiry date (optional)" data-testid="promo-code-expiry" />
            <button disabled={creating} className="h-10 px-5 rounded-full bg-gold hover:bg-gold-hover text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="promo-code-create">
              <PlusCircle size={15} /> {creating ? "Creating…" : "Create code"}
            </button>
          </div>
        </form>

        <div className="bg-white rounded-3xl border border-slate-200/80 p-5" data-testid="promo-codes-table">
          {!codes ? (
            <p className="text-sm text-slate-400 text-center py-6">Loading codes…</p>
          ) : codes.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6" data-testid="promo-codes-empty">No promo codes yet — create your first campaign code above.</p>
          ) : (
            <div className="space-y-2">
              <div className="hidden lg:grid grid-cols-[130px_1fr_90px_90px_110px_110px_110px_150px] gap-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                <span>Code</span><span>Campaign</span><span>Discount</span>
                <span className="text-right flex items-center justify-end gap-1"><ShoppingBag size={10} /> Orders</span>
                <span className="text-right flex items-center justify-end gap-1"><Users size={10} /> Customers</span>
                <span className="text-right">Discount given</span><span className="text-right">Revenue</span><span></span>
              </div>
              {codes.map((c) => (
                <div key={c.id} className={`grid lg:grid-cols-[130px_1fr_90px_90px_110px_110px_110px_150px] gap-2 items-center rounded-2xl border p-3 text-sm ${c.active ? "border-gold/40 bg-gold/5" : "border-slate-100 opacity-70"}`} data-testid={`promo-code-row-${c.code}`}>
                  <span className="font-mono font-extrabold text-navy">{c.code}</span>
                  <span className="text-slate-600 truncate">
                    {c.description || "—"}
                    {c.expires_at && <span className="block text-[10px] text-slate-400">expires {new Date(c.expires_at).toLocaleDateString("en-ZA")}</span>}
                    {c.min_subtotal > 0 && <span className="block text-[10px] text-slate-400">min order {formatR(c.min_subtotal)}</span>}
                  </span>
                  <span className="font-bold text-brand">{c.discount_type === "percent" ? `${c.discount_value}%` : formatR(c.discount_value)}</span>
                  <span className="lg:text-right font-mono font-bold text-navy" data-testid={`promo-code-uses-${c.code}`}>{c.uses}</span>
                  <span className="lg:text-right font-mono font-bold text-navy" data-testid={`promo-code-customers-${c.code}`}>{c.unique_customers}</span>
                  <span className="lg:text-right font-mono text-slate-500">{formatR(c.discount_given)}</span>
                  <span className="lg:text-right font-mono text-slate-500">{formatR(c.revenue)}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => downloadCard(c)} className="h-8 px-2.5 rounded-full border border-navy/30 text-navy text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-navy hover:text-white transition-colors" title="Download ready-to-print A6 handout card" data-testid={`promo-code-card-${c.code}`}>
                      <Download size={11} /> Card
                    </button>
                    <button onClick={() => toggleCode(c)} className={`h-8 px-2.5 rounded-full text-[11px] font-bold flex items-center justify-center gap-1 transition-colors ${c.active ? "border border-red-200 text-red-500 hover:bg-red-50" : "border border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`} data-testid={`promo-code-toggle-${c.code}`}>
                      <Power size={11} /> {c.active ? "Off" : "On"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold text-navy mb-1">Product sale prices</h2>
        <p className="text-sm text-slate-500 mb-5">
          Set or schedule promotional prices on any product via <span className="font-semibold text-navy">Products → Edit</span>. They activate and expire automatically by date and apply across catalogue, cart and checkout.
        </p>
        <div className="grid lg:grid-cols-3 gap-5">
          <PromoGroup title="Active now" products={data.active} tone="active" onEnd={endPromo} />
          <PromoGroup title="Scheduled" products={data.scheduled} tone="scheduled" onEnd={endPromo} />
          <PromoGroup title="Expired" products={data.expired} tone="expired" onEnd={endPromo} />
        </div>
      </section>
    </div>
  );
}
